/* SPDX-License-Identifier: Apache-2.0 */
/* SPDX-FileCopyrightText: 2021-2023 OKTET Labs Ltd. */
import { ReactElement } from 'react';
import { z } from 'zod';

import { analyticsEventNames, trackEvent } from '@/bublik/features/analytics';
import { DashboardCellData } from '@/shared/types';
import { cn, cva, Tooltip } from '@/shared/tailwind-ui';
import {
	LinkWithProject,
	useNavigateWithProject
} from '@/bublik/features/projects';

import { getUrl } from '../../utils';

const UNEXPECTED_COLUMN_KEY = 'unexpected';
const PATH_TO_RUN = 'runs';

export const linkStyles = cva({
	base: [
		'py-0.5 px-2 truncate rounded hover:underline',
		'text-[0.75rem] font-medium leading-[1.125rem]'
	]
});

export interface CellLinkProps {
	cellKey: string;
	data: DashboardCellData;
	bgColor?: string;
	hint?: string;
}

export function CellLink({
	data,
	bgColor = 'bg-badge-0',
	cellKey,
	hint
}: CellLinkProps) {
	const cellString = data.value ?? '';
	const destination = getDestinationHint(hint);

	if (!data.payload?.url) {
		return <span className={cn(linkStyles(), bgColor)}>{cellString}</span>;
	}

	const isAbsoluteUrl = z.string().url().safeParse(data.payload.url).success;
	const isLinkToRun =
		cellKey === UNEXPECTED_COLUMN_KEY && data.payload.url === PATH_TO_RUN;

	// 1. Absolute URL
	if (isAbsoluteUrl) {
		return (
			<AbsoluteLink
				cellString={cellString}
				cellKey={cellKey}
				url={data.payload.url}
				bgColor={bgColor}
				destination={destination}
			/>
		);
	}

	const to = getUrl(data.payload.url, data.payload?.params);

	// 2. Link to run with unexpected open on CTRL click
	if (isLinkToRun) {
		return (
			<LinkToRun
				to={to}
				cellKey={cellKey}
				cellString={cellString}
				bgColor={bgColor}
				destination={destination}
			/>
		);
	}

	// 3. Relative URL
	return (
		<LinkHintCard destination={destination}>
			<LinkWithProject
				to={getUrl(data.payload.url, data.payload?.params)}
				onClick={() => {
					trackDashboardTableLinkClick({
						cellKey,
						destinationType: 'relative'
					});
				}}
				className={cn(linkStyles(), bgColor)}
			>
				{cellString}
			</LinkWithProject>
		</LinkHintCard>
	);
}

interface LinkToRunProps {
	cellKey: string;
	cellString: string | number;
	to: string;
	bgColor?: string;
	destination?: string;
}

function LinkToRun(props: LinkToRunProps) {
	const { cellString, cellKey, to, bgColor, destination } = props;
	const navigate = useNavigateWithProject();

	return (
		<LinkHintCard destination={destination}>
			<LinkWithProject
				to={to}
				state={{ openUnexpected: true }}
				onClick={(e) => {
					trackDashboardTableLinkClick({
						cellKey,
						destinationType: 'run',
						hasCtrlKey: e.ctrlKey,
						interaction: 'click'
					});

					e.preventDefault();
					if (e.ctrlKey) {
						navigate(to, { state: { openUnexpectedResults: true } });
					} else {
						navigate(to, { state: { openUnexpected: true } });
					}
				}}
				onContextMenu={(e) => {
					trackDashboardTableLinkClick({
						cellKey,
						destinationType: 'run',
						hasCtrlKey: e.ctrlKey,
						interaction: 'context_menu'
					});

					e.preventDefault();
					if (e.ctrlKey) {
						navigate(to, { state: { openUnexpectedResults: true } });
					} else {
						navigate(to, { state: { openUnexpected: true } });
					}
				}}
				className={cn(linkStyles(), bgColor)}
			>
				{cellString}
			</LinkWithProject>
		</LinkHintCard>
	);
}

interface AbsoluteLinkProps {
	cellKey: string;
	cellString: string | number;
	url: string;
	bgColor?: string;
	destination?: string;
}

function AbsoluteLink(props: AbsoluteLinkProps) {
	const { cellString, cellKey, bgColor, url, destination } = props;

	return (
		<LinkHintCard destination={destination}>
			<a
				rel="noopener noreferrer"
				href={url}
				target="_blank"
				onClick={() => {
					trackDashboardTableLinkClick({
						cellKey,
						destinationType: 'absolute'
					});
				}}
				className={cn(linkStyles(), bgColor)}
			>
				{cellString}
			</a>
		</LinkHintCard>
	);
}

interface LinkHintCardProps {
	destination?: string;
	children: ReactElement;
}

function LinkHintCard({ destination, children }: LinkHintCardProps) {
	return (
		<Tooltip content={destination ?? ''} disabled={!destination}>
			{children}
		</Tooltip>
	);
}

function getDestinationHint(hint: string | undefined) {
	if (!hint) return undefined;

	return hint.trim();
}

type DashboardTableLinkClickDestinationType = 'absolute' | 'relative' | 'run';

interface DashboardTableLinkClickPayload {
	cellKey: string;
	destinationType: DashboardTableLinkClickDestinationType;
	interaction?: 'click' | 'context_menu';
	hasCtrlKey?: boolean;
}

function trackDashboardTableLinkClick(payload: DashboardTableLinkClickPayload) {
	trackEvent(analyticsEventNames.dashboardTableLinkClick, {
		cellKey: payload.cellKey,
		destinationType: payload.destinationType,
		interaction: payload.interaction ?? 'click',
		hasCtrlKey: payload.hasCtrlKey ?? false
	});
}
